import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuarantine, useSpamDecision } from '../api/hooks'
import type { DocumentOut } from '../api/models'
import { Badge, EmptyState, ErrorState, LoadingBlock, formatDate } from '../components/ui'

const TABS = [
  { key: 'suspected', label: 'Zu prüfen' },
  { key: 'quarantined', label: 'Aussortiert' },
  { key: 'all', label: 'Alle' },
] as const

/**
 * Everything the filter pulled out of the normal flow. Nothing here was deleted - it is
 * set aside and kept out of the global search, and the user can overturn any call.
 */
export function QuarantinePage() {
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('suspected')
  const query = useQuarantine(tab)
  const decide = useSpamDecision()

  return (
    <>
      <h1 className="mb-2 text-2xl">Spam &amp; Betrug</h1>
      <p className="mb-4 max-w-prose text-sm text-paper-600 dark:text-paper-300">
        Diese Nachrichten sind aus der Suche und den normalen Listen ausgeschlossen —
        gelöscht wird nichts. Unter <strong>Zu prüfen</strong> stehen die Fälle, bei denen
        sich Backoffice nicht sicher ist.
      </p>

      <div role="tablist" aria-label="Filter" className="mb-4 flex flex-wrap gap-1">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={`btn ${tab === entry.key ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {query.isPending ? (
        <LoadingBlock label="Wird geladen" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title={tab === 'suspected' ? 'Nichts zu entscheiden' : 'Nichts aussortiert'}
          description="Sobald etwas verdächtig aussieht, taucht es hier auf."
        />
      ) : (
        <>
          <p className="mb-2 text-sm text-paper-500" role="status">
            {query.data.total} Nachrichten
          </p>
          <ul className="space-y-2">
            {(query.data.items as DocumentOut[]).map((document) => (
              <li key={document.id} className="card">
                <div className="flex flex-wrap items-start gap-2">
                  <Link
                    to="/dokumente/$documentId"
                    params={{ documentId: document.id }}
                    className="min-w-0 flex-1 font-medium hover:underline"
                  >
                    {document.title || 'Ohne Betreff'}
                  </Link>
                  <Badge tone={document.spam_state === 'quarantined' ? 'danger' : 'warn'}>
                    {document.spam_state === 'quarantined'
                      ? 'wahrscheinlich Betrug'
                      : 'unsicher — bitte prüfen'}
                  </Badge>
                  <span className="chip" title="Wie sicher sich die Prüfung ist">
                    {Math.round(Number(document.spam_score) * 100)} %
                  </span>
                </div>

                {document.spam_reasons?.length ? (
                  <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-paper-600 dark:text-paper-300">
                    {document.spam_reasons.slice(0, 4).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                ) : null}

                <p className="mt-1 text-xs text-paper-500">
                  {formatDate(document.document_date ?? document.created_at)}
                  {document.spam_decided_by_user ? ' · von dir entschieden' : ''}
                </p>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: document.id, decision: 'ok' })}
                  >
                    Ist in Ordnung
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-red-700 dark:text-red-300"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: document.id, decision: 'spam' })}
                  >
                    Ist Betrug
                  </button>
                  <Link
                    to="/dokumente/$documentId"
                    params={{ documentId: document.id }}
                    className="btn-ghost"
                  >
                    Ansehen
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}
